/**
 * En la ficha: quién responde por este paciente, y de quién responde él.
 *
 * Son las dos caras de la misma relación y las dos hacen falta en el mostrador:
 * con un niño delante hay que saber a quién llamar; con la madre delante,
 * pidiendo cita "para mi hijo", hay que llegar a la ficha del hijo sin buscarlo
 * por un apellido que a veces ni coincide.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Baby, Phone, ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import { Link } from "react-router-dom";
import {
  type Apoderado,
  type Paciente,
  agregarApoderado,
  listApoderados,
  menoresACargo,
  quitarApoderado,
} from "../../api/pacientes";
import { formatApiError } from "../../api/client";
import { useBase } from "../../auth/TenantBrandingProvider";
import { formatearTelefono } from "../TelefonoInput";
import { ApoderadosForm, type LineaApoderado } from "./ApoderadosForm";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
} from "../ui";

const PARENTESCO_LABEL: Record<string, string> = {
  MADRE: "Madre",
  PADRE: "Padre",
  ABUELA: "Abuela",
  ABUELO: "Abuelo",
  TIA: "Tía",
  TIO: "Tío",
  HERMANA: "Hermana",
  HERMANO: "Hermano",
  TUTOR: "Tutor legal",
  OTRO: "Otro",
};

export function ApoderadosCard({
  paciente,
  puedeEditar,
}: {
  paciente: Paciente;
  puedeEditar: boolean;
}) {
  const base = useBase();
  const queryClient = useQueryClient();
  const [agregarOpen, setAgregarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Quitar un apoderado se confirma: es el contacto de un menor, y un clic sin
  // querer en la papelera deja al niño sin a quién llamar.
  const [porQuitar, setPorQuitar] = useState<Apoderado | null>(null);

  const { data: apoderados = [] } = useQuery({
    queryKey: ["apoderados", paciente.id],
    queryFn: () => listApoderados(paciente.id),
  });
  const { data: aCargo = [] } = useQuery({
    queryKey: ["a-cargo", paciente.id],
    queryFn: () => menoresACargo(paciente.id),
  });

  const quitar = useMutation({
    mutationFn: (vinculoId: number) => quitarApoderado(paciente.id, vinculoId),
    onSuccess: () => {
      setError(null);
      setPorQuitar(null);
      queryClient.invalidateQueries({ queryKey: ["apoderados", paciente.id] });
      queryClient.invalidateQueries({ queryKey: ["paciente", paciente.id] });
    },
    onError: (e) => {
      setPorQuitar(null);
      setError(formatApiError(e, "No se pudo quitar el apoderado."));
    },
  });

  // Un adulto sin apoderados ni menores a cargo no necesita esta tarjeta.
  if (!paciente.es_menor && apoderados.length === 0 && aCargo.length === 0) return null;

  return (
    <>
      {(paciente.es_menor || apoderados.length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="flex items-center gap-2 font-semibold text-slate-900">
                  <Users className="h-4 w-4 text-[color:var(--color-link)]" />
                  Apoderados
                  {paciente.es_menor && (
                    <Badge variant="warning">
                      Menor · {paciente.edad_anios} años
                    </Badge>
                  )}
                </h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  A quién llamar y quién puede autorizar.
                </p>
              </div>
              {puedeEditar && (
                <Button variant="ghost" onClick={() => setAgregarOpen(true)}>
                  <UserPlus className="h-4 w-4" />
                  Agregar
                </Button>
              )}
            </div>
          </CardHeader>
          <CardBody className="space-y-2">
            {error && <Alert>{error}</Alert>}
            {apoderados.length === 0 && (
              <Alert variant="error">
                Este paciente es menor y no tiene apoderado registrado. Agrégalo:
                sin él no hay a quién avisar de un resultado.
              </Alert>
            )}
            {apoderados.map((a) => (
              <div
                key={a.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 px-3 py-2"
              >
                <div className="min-w-0 grow">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
                    {a.paciente_id ? (
                      <Link
                        to={`${base}/pacientes/${a.paciente_id}`}
                        className="text-[color:var(--color-link)] hover:underline"
                      >
                        {a.nombre_completo}
                      </Link>
                    ) : (
                      a.nombre_completo
                    )}
                    <Badge variant="neutral">
                      {PARENTESCO_LABEL[a.parentesco] ?? a.parentesco}
                    </Badge>
                    {a.es_principal && <Badge variant="success">Llamar primero</Badge>}
                    {a.puede_autorizar && (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-slate-500"
                        title="Puede firmar consentimientos y autorizar procedimientos"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Autoriza
                      </span>
                    )}
                  </p>
                  <p className="font-mono text-xs text-slate-500">
                    {a.tipo_documento} {a.num_documento}
                    {a.paciente_id ? " · también es paciente" : ""}
                  </p>
                </div>
                {a.telefono && (
                  <a
                    href={`tel:${a.telefono}`}
                    className="inline-flex items-center gap-1.5 font-mono text-sm text-[color:var(--color-link)] hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {formatearTelefono(a.telefono)}
                  </a>
                )}
                {puedeEditar && (
                  <button
                    type="button"
                    onClick={() => setPorQuitar(a)}
                    className="text-slate-400 hover:text-red-600"
                    aria-label="Quitar apoderado"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {aCargo.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <Baby className="h-4 w-4 text-[color:var(--color-link)]" />
              A su cargo
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Pacientes por los que esta persona responde.
            </p>
          </CardHeader>
          <CardBody>
            <ul className="divide-y divide-slate-100">
              {aCargo.map((m) => (
                <li key={m.paciente_id} className="flex items-center gap-3 py-2 text-sm">
                  <Link
                    to={`${base}/pacientes/${m.paciente_id}`}
                    className="font-medium text-[color:var(--color-link)] hover:underline"
                  >
                    {m.nombre_completo}
                  </Link>
                  <Badge variant="neutral">
                    {PARENTESCO_LABEL[m.parentesco] ?? m.parentesco}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {agregarOpen && (
        <AgregarApoderadoDialog
          pacienteId={paciente.id}
          onClose={() => setAgregarOpen(false)}
          onDone={() => {
            setAgregarOpen(false);
            setError(null);
            queryClient.invalidateQueries({ queryKey: ["apoderados", paciente.id] });
            queryClient.invalidateQueries({ queryKey: ["paciente", paciente.id] });
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        open={porQuitar !== null}
        onClose={() => setPorQuitar(null)}
        onConfirm={() => porQuitar && quitar.mutate(porQuitar.id)}
        loading={quitar.isPending}
        title="Quitar apoderado"
        description={
          porQuitar?.es_principal
            ? "Es a quien se llama primero por este paciente. Si lo quitas, asegúrate de que otro quede como contacto principal."
            : "Deja de figurar como apoderado de este paciente. La persona no se borra: sigue registrada."
        }
        detalle={
          porQuitar && (
            <>
              <span className="font-medium">{porQuitar.nombre_completo}</span>
              {" · "}
              {PARENTESCO_LABEL[porQuitar.parentesco] ?? porQuitar.parentesco}
              {porQuitar.telefono && (
                <span className="ml-2 font-mono text-xs text-slate-500">
                  {formatearTelefono(porQuitar.telefono)}
                </span>
              )}
            </>
          )
        }
        confirmLabel="Sí, quitarlo"
      />
    </>
  );
}

function AgregarApoderadoDialog({
  pacienteId,
  onClose,
  onDone,
  onError,
}: {
  pacienteId: number;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [lineas, setLineas] = useState<LineaApoderado[]>([]);

  const guardar = useMutation({
    mutationFn: async () => {
      for (const l of lineas) {
        const { _nombre, _yaExistia, _aCargoDe, ...datos } = l;
        await agregarApoderado(pacienteId, {
          ...datos,
          telefono: datos.telefono || undefined,
          email: datos.email || undefined,
        });
      }
    },
    onSuccess: onDone,
    onError: (e) => onError(formatApiError(e, "No se pudo agregar el apoderado.")),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Agregar apoderado"
      description="Se busca por DNI: si ya está en el sistema, no se duplica."
      size="lg"
    >
      <div className="space-y-4">
        <ApoderadosForm valor={lineas} onChange={setLineas} obligatorio={false} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => guardar.mutate()}
            disabled={lineas.length === 0 || guardar.isPending}
          >
            Guardar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
